import { Engineering, PhotoLibrary, WavingHand } from '@mui/icons-material';
import {
  Box,
  Card,
  Dialog,
  Divider,
  FormGroup,
  Grid2,
  Paper,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { OperationalFaultIcon, TechnicalFaultIcon } from 'assets/custom_icons/customIcons';
import { type FC, useContext, useEffect, useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthContext, VisitDashboardContext } from '../../contexts';
import {
  CommentType,
  type ExtendedCommentModel,
  FaultCategory,
  type FaultData,
  type FaultInteractionData,
  markFaultAsResolved,
  postResolveFault
} from '../../models';
import { composeVisitFaultName } from '../../utils';
import { AvaniButton } from '../Buttons';
import { AmsPartScrollGallery, MediasScrollableGallery, OverflowingContent } from '../DataDisplay';
import { FaultInteractionsDropdown } from '../Dropdowns';
import { FaultStatus } from '../Faults';
import { OpenAvaniShop } from '../Functions';

const AvaniStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
  }
`
);

const FormGroupShared = styled(FormGroup)(
  () => `
  margin: 24px 12px;
`
);

const FaultButton = styled(Paper)(
  ({ theme }) => `
  padding: 5px;
  border-radius: 16px;
  background-color: ${theme.colors.avani.turc};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  flex-basis: 0;
  cursor: pointer;
`
);

type ActionDialogContentsProps = {
  faultId: string;
  resolvedStatus: boolean;
  onCancel: () => void;
};

const ActionDialogContents: FC<ActionDialogContentsProps> = ({ resolvedStatus, faultId, onCancel }) => {
  const { toggleDashboardsUpdate, setLoading } = useContext(VisitDashboardContext);
  const theme = useTheme();
  const actionName = resolvedStatus ? 'Post Comment' : 'Mark Resolved';
  const [comment, setComment] = useState<string>('');
  const { auth } = useAuthContext();

  const attemptResolveFault = async (comment: string | null) => {
    if (!auth) return;

    setLoading(true);

    const commentObj: ExtendedCommentModel = {
      content: comment ?? '',
      type: resolvedStatus ? CommentType.FaultClientPostResolution : CommentType.FaultClientResolution,
      author: auth,
      authorId: auth.id,
      faultId: faultId,
      id: `${Date.now()}.${Math.random().toString().substring(2)}`
    };
    try {
      if (resolvedStatus) {
        await postResolveFault(faultId, commentObj);
      } else {
        await markFaultAsResolved(faultId, commentObj);
      }
      toggleDashboardsUpdate();
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <Typography variant="h5" fontSize={32} paddingLeft={'12px'}>
          {actionName}
        </Typography>
        <FormGroupShared>
          <TextField
            id={useId()}
            multiline
            minRows={3}
            label="Add Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </FormGroupShared>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', padding: '0 12px' }}>
          <AvaniButton
            label={'Cancel'}
            loading={false}
            onClick={() => onCancel()}
            sx={{
              backgroundColor: 'transparent',
              color: theme.colors.avani.magenta,
              border: `2px solid ${theme.colors.avani.magenta}`,
              margin: 0,
              [`:hover`]: { borderWidth: '2px', backgroundColor: theme.colors.avani.magenta, color: 'white' }
            }}
          />
          <AvaniButton
            label={actionName}
            loading={false}
            disabled={!comment}
            sx={{ border: `2px solid ${theme.colors.avani.magenta}`, [`:hover`]: { borderWidth: '2px' } }}
            onClick={() => attemptResolveFault(comment)}
          />
        </Box>
      </Box>
    </>
  );
};

export const AvaniFaultCard: FC<FaultData> = (fault) => {
  const {
    id,
    category,
    isCritical,
    isResolved,
    technicianName,
    faultInteractions,
    isPendingRevision,
    hasPostResolutionComment,
    amsPartsUsed
  } = fault;
  const theme = useTheme();
  const navigate = useNavigate();
  const { visitId } = useParams();

  const [actionOpen, setActionOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeInteraction, setActiveInteraction] = useState<FaultInteractionData | null>(null);

  useEffect(() => {
    if (!fault) return;
    setActiveInteraction(faultInteractions.find((vfi) => vfi.visitId === visitId) ?? null);
    //todo:v2-changes
  }, [fault]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', width: '100%', alignContent: 'end' }}>
        <div style={{ flexGrow: 1 }}></div>
        <Card
          sx={{
            display: 'inline-flex',
            padding: '5px 9px 0 9px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            marginRight: 2,
            backgroundColor: !isCritical ? theme.colors.avani.turc : theme.colors.avani.magenta
          }}
          elevation={0}
        >
          <Typography variant={'body1'} sx={{ color: 'white' }}>
            {isCritical ? 'Critical' : 'Non Critical'}
          </Typography>
        </Card>
      </Box>
      <AvaniStyledCard
        raised={true}
        sx={{
          height: '100%',
          backgroundColor: !isResolved
            ? isPendingRevision
              ? theme.colors.avani.aYellow
              : theme.colors.avani.lightMagenta
            : theme.colors.avani.lightTurc
        }}
      >
        <Grid2 container columns={6} sx={{ gap: 0 }} alignItems="center">
          <Grid2 size={{ xs: 6 }} sx={{ display: 'flex', mt: 1, minHeight: 50 }} alignItems="center">
            <Typography
              variant={'h5'}
              width={'100%'}
              component={'h5'}
              fontSize={18}
              fontWeight={'bold'}
              sx={{ color: theme.colors.avani.teal, flexGrow: 1, cursor: 'pointer', [`:hover`]: { opacity: 0.6 } }}
              title={composeVisitFaultName(fault)}
              onClick={() => navigate(`/dashboard/faults/${id}`)}
            >
              {composeVisitFaultName(fault)}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 1 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }} container justifyContent="space-between" alignItems={'center'}>
            <Grid2>
              {!!faultInteractions.length && activeInteraction && (
                <FaultInteractionsDropdown
                  dates
                  faultInteractions={faultInteractions}
                  activeInteraction={activeInteraction}
                  setActiveInteraction={setActiveInteraction}
                />
              )}
            </Grid2>
            <Grid2 sx={{ padding: '0 8px 8px 8px' }}>
              <div style={{ height: '24px', textAlign: 'center' }}>
                {category === FaultCategory.Operational
                  ? OperationalFaultIcon({ width: 32, height: 32 })
                  : TechnicalFaultIcon({ width: 32, height: 32 })}
              </div>
              <Typography
                fontSize={16}
                lineHeight={1}
                variant={'body1'}
                sx={{ color: theme.colors.avani.turc, textAlign: 'center' }}
              >
                {category}
              </Typography>
            </Grid2>
            <Grid2
              sx={{
                alignContent: 'center',
                [`& .MuiBox-root > .MuiBox-root`]: { display: 'inline!important' },
                [`& span.MuiTypography-h4`]: { display: 'inline!important', paddingLeft: isMobile ? '2px' : '5px' }
              }}
            >
              <FaultStatus visitFault={fault} />
            </Grid2>
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 1 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
                Description
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Engineering sx={{ display: 'inline', float: 'left', color: theme.colors.avani.turc, fontSize: 18 }} />
                <Typography fontWeight={'bold'} fontSize={14}>
                  {technicianName}
                </Typography>
              </Box>
            </Box>
            <OverflowingContent textContent={activeInteraction?.description ?? ''} heightSpan={60} />
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 1 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
              Recommendation
            </Typography>
            <OverflowingContent textContent={activeInteraction?.actionRecommendation ?? ''} heightSpan={60} />
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 1 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }} container columns={21} justifyContent="space-between">
            <Grid2 size={{ xs: 10 }}>
              <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
                Multimedia
              </Typography>
              <MediasScrollableGallery medias={activeInteraction?.medias} keepHeight />
            </Grid2>
            <Grid2 size={{ xs: 10 }}>
              <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
                AMS Parts
              </Typography>
              <AmsPartScrollGallery amsPartsUsed={amsPartsUsed} keepHeight />
            </Grid2>
          </Grid2>

          <Grid2 size={{ xs: 6 }} sx={{ py: 1 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ p: 0, paddingLeft: 0 }}>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              <FaultButton sx={{ maxWidth: 130 }} elevation={0} onClick={() => navigate(`/dashboard/faults/${id}`)}>
                <Typography variant={'body1'} lineHeight={1} sx={{ textAlign: 'center', mt: '5px', paddingRight: 1 }}>
                  <PhotoLibrary sx={{ color: 'white' }} />
                </Typography>
                <Typography variant={'body1'} sx={{ color: 'white', textAlign: 'center' }}>
                  View
                </Typography>
              </FaultButton>
              <FaultButton sx={{ maxWidth: 130 }} elevation={0} onClick={() => OpenAvaniShop()}>
                <Box sx={{ textAlign: 'center', paddingTop: 1, paddingRight: 1 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M3.5 2C2.67157 2 2 2.67157 2 3.5C2 4.32843 2.67157 5 3.5 5C4.32843 5 5 4.32843 5 3.5C5 2.67157 4.32843 2 3.5 2ZM20.3125 2.3125L16.9688 9C15.8656 8.50508 14.43 8.73407 13.5312 10.0312L13.4062 9.96875C12.6276 11.6489 10.7462 12.8687 8.875 13.5938C7.93939 13.9563 7.00358 14.201 6.28125 14.3438C5.55892 14.4865 4.89169 14.488 5 14.5L3.90625 14.375V15.5V15.5938V15.9688L4.15625 16.25C8.98374 21.8284 16.375 22 16.375 22H16.7812L17.0938 21.7188C17.0938 21.7188 20.1903 18.7591 20.875 14.1875H20.9062C21.4062 11.9875 20.7938 10.6875 19.5938 10.1875C19.5766 10.1704 19.4815 10.1202 19.4375 10.0938L22 3.09375L20.3125 2.3125ZM8 3C7.44772 3 7 3.44772 7 4C7 4.55228 7.44772 5 8 5C8.55228 5 9 4.55228 9 4C9 3.44772 8.55228 3 8 3ZM6 7C4.89543 7 4 7.89543 4 9C4 10.1046 4.89543 11 6 11C7.10457 11 8 10.1046 8 9C8 7.89543 7.10457 7 6 7ZM14.8125 11.5625L18.9375 13.3438C18.6037 17.0513 16.4011 19.4516 15.9688 19.9062C15.7499 19.8936 15.0719 19.8306 14.1562 19.625C14.9021 18.7878 16.0812 17.2086 16.6875 15.1875C14.8915 17.2709 12.9875 18.4548 12.0312 18.9688C11.4469 18.7551 10.8555 18.5097 10.2188 18.1875C10.8532 17.6437 11.8262 16.7188 12.4062 15.6875C11.6618 16.4996 10.0911 17.2128 9.125 17.5938C8.46445 17.1943 7.80611 16.7347 7.15625 16.1875C7.89966 16.0209 8.69995 15.8151 9.59375 15.4688C11.4939 14.7324 13.5867 13.5334 14.8125 11.5625Z"
                      fill="white"
                    />
                  </svg>
                </Box>
                <Typography variant={'body1'} sx={{ color: 'white', textAlign: 'center' }}>
                  Services
                </Typography>
              </FaultButton>

              {!isPendingRevision && !hasPostResolutionComment && (
                <FaultButton elevation={0} onClick={() => setActionOpen(true)}>
                  <Typography variant={'body1'} lineHeight={1} sx={{ textAlign: 'center', mt: '5px', paddingRight: 1 }}>
                    <WavingHand sx={{ color: 'white' }} />
                  </Typography>
                  <Typography variant={'body1'} sx={{ color: 'white', textAlign: 'center' }}>
                    {isResolved ? 'Post Comment' : 'Mark Resolved'}
                  </Typography>
                </FaultButton>
              )}
            </Box>
            <Dialog open={actionOpen} closeAfterTransition={false} keepMounted>
              <ActionDialogContents faultId={id} resolvedStatus={isResolved} onCancel={() => setActionOpen(false)} />
            </Dialog>
          </Grid2>
        </Grid2>
      </AvaniStyledCard>
    </Box>
  );
};
